import React from "react";
import { ExpenseUserOption } from "../../shared/expenseUsersApi";
import SearchableMultiSelect from "../controls/SearchableMultiSelect";
import OcrReviewFlag from "./OcrReviewFlag";
import {
  ReceiptConfidenceFields,
  ReceiptReviewDraft,
  ReceiptReviewErrors,
  receiptFieldClass,
} from "./receiptReviewTypes";

interface ReceiptDetailsFieldsProps {
  draft: ReceiptReviewDraft;
  fields: ReceiptConfidenceFields;
  userOptions: ExpenseUserOption[];
  userOptionsLoading: boolean;
  errors?: ReceiptReviewErrors;
  onChange: (name: keyof ReceiptReviewDraft, value: string) => void;
}

export default function ReceiptDetailsFields({
  draft,
  fields,
  userOptions,
  userOptionsLoading,
  errors = {},
  onChange,
}: ReceiptDetailsFieldsProps) {
  const selectedUser = userOptions.find((option) => String(option.value) === String(draft.user_id));

  return (
    <div className="space-y-4">
      <SearchableMultiSelect
        inputId="expense-assigned-user"
        label="Assigned user"
        options={userOptions}
        value={selectedUser ? [selectedUser.value] : []}
        disabled={userOptionsLoading}
        required
        placeholder={userOptionsLoading ? "Loading users…" : "Select a user"}
        selectionLimit={1}
        onChange={(selectedUsers) => onChange("user_id", String(selectedUsers[0] ?? ""))}
      />
      <label className="block text-sm font-medium">
        Vendor <OcrReviewFlag confidence={fields.vendor?.confidence} />
        <input
          required
          className={receiptFieldClass}
          value={draft.vendor}
          onChange={(event) => onChange("vendor", event.target.value)}
        />
        {errors.vendor && <span className="mt-1 block text-sm text-red-600">{errors.vendor}</span>}
      </label>
      <label className="block text-sm font-medium">
        Address <OcrReviewFlag confidence={fields.vendor_address?.confidence} />
        <input
          className={receiptFieldClass}
          value={draft.vendor_address}
          onChange={(event) => onChange("vendor_address", event.target.value)}
        />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-sm font-medium">
          Date <OcrReviewFlag confidence={fields.date?.confidence} />
          <input
            type="date"
            className={receiptFieldClass}
            value={draft.date}
            onChange={(event) => onChange("date", event.target.value)}
          />
        </label>
        <label className="block text-sm font-medium">
          Currency <OcrReviewFlag confidence={fields.currency?.confidence} />
          <input
            required
            maxLength={3}
            className={receiptFieldClass}
            value={draft.currency}
            onChange={(event) => onChange("currency", event.target.value.toUpperCase())}
          />
        </label>
        <label htmlFor="expense-type" className="col-span-2 block text-sm font-medium">
          Expense type
          <select
            id="expense-type"
            required
            className={receiptFieldClass}
            value={String(draft.expense_type ?? "")}
            onChange={(event) => onChange("expense_type", event.target.value)}
          >
            <option value="">Select an expense type</option>
            <option value="1">Receipt</option>
          </select>
          {errors.expense_type && (
            <span className="mt-1 block text-sm text-red-600">{errors.expense_type}</span>
          )}
        </label>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <label className="block text-sm font-medium">
          Subtotal <OcrReviewFlag confidence={fields.subtotal?.confidence} />
          <input
            type="number"
            min="0"
            step="0.01"
            className={receiptFieldClass}
            value={draft.sub_total_amount}
            onChange={(event) => onChange("sub_total_amount", event.target.value)}
          />
          {errors.sub_total_amount && (
            <span className="mt-1 block text-sm text-red-600">{errors.sub_total_amount}</span>
          )}
        </label>
        <label className="block text-sm font-medium">
          Tax <OcrReviewFlag confidence={fields.tax?.confidence} />
          <input
            type="number"
            min="0"
            step="0.01"
            className={receiptFieldClass}
            value={draft.tax_amount}
            onChange={(event) => onChange("tax_amount", event.target.value)}
          />
          {errors.tax_amount && (
            <span className="mt-1 block text-sm text-red-600">{errors.tax_amount}</span>
          )}
        </label>
        <label className="block text-sm font-medium">
          Total <OcrReviewFlag confidence={fields.total?.confidence} />
          <input
            required
            type="number"
            min="0"
            step="0.01"
            className={receiptFieldClass}
            value={draft.total_amount}
            onChange={(event) => onChange("total_amount", event.target.value)}
          />
          {errors.total_amount && (
            <span className="mt-1 block text-sm text-red-600">{errors.total_amount}</span>
          )}
        </label>
      </div>
      <label className="block text-sm font-medium">
        Notes
        <textarea
          rows={2}
          className={receiptFieldClass}
          value={draft.notes}
          onChange={(event) => onChange("notes", event.target.value)}
        />
      </label>
    </div>
  );
}
